import { computed, signal } from '@preact/signals';

export interface Job {
  id: string;
  kind: string; // e.g. 'meta.sync', 'shopify.sync'
  label?: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  progress?: number; // 0..1
  startedAt?: string | null;
  finishedAt?: string | null;
  errorMessage?: string | null;
  /** Arbitrary result payload the backend attaches when the job succeeds. */
  resultData?: Record<string, unknown> | null;
  /** Epoch ms of the most recent poll that observed this job. Used to auto-evict stale entries. */
  lastSeenAt?: number;
}

/**
 * Jobs older than this without a poll update are considered abandoned (poller died,
 * tab was closed mid-run, etc.) and treated as "not running" for banner purposes.
 */
const STALE_AFTER_MS = 30_000;

export const activeJobs = signal<Job[]>([]);

function isLive(j: Job): boolean {
  if (j.status !== 'queued' && j.status !== 'running') return false;
  if (j.lastSeenAt == null) return true; // legacy entry, trust it
  return Date.now() - j.lastSeenAt < STALE_AFTER_MS;
}

export const runningCount = computed(() => activeJobs.value.filter(isLive).length);

export function upsertJob(job: Job) {
  const stamped: Job = { ...job, lastSeenAt: job.lastSeenAt ?? Date.now() };
  const idx = activeJobs.value.findIndex((j) => j.id === stamped.id);
  if (idx === -1) {
    activeJobs.value = [...activeJobs.value, stamped];
  } else {
    const next = activeJobs.value.slice();
    next[idx] = stamped;
    activeJobs.value = next;
  }
}

export function removeJob(id: string) {
  activeJobs.value = activeJobs.value.filter((j) => j.id !== id);
}

// Garbage-collect abandoned jobs (e.g. user navigated away mid-poll). Without this,
// the sync banner would show "running" forever for jobs whose poller is dead.
if (typeof window !== 'undefined') {
  setInterval(() => {
    const stale = activeJobs.value.filter(
      (j) => !isLive(j) && (j.status === 'queued' || j.status === 'running'),
    );
    if (stale.length === 0) return;
    activeJobs.value = activeJobs.value.filter((j) => !stale.includes(j));
  }, 15_000);
}
